<script setup lang="ts">
  import { computed } from 'vue';
  import { Loader2 } from 'lucide-vue-next';
  import { useRouter } from 'vue-router';
  import { useTableFilterModal, useBulkDelete } from '@/shared/lib';
  import { Pagination } from '@/shared/ui';
  import type { NoticeGeneralHistoryItem } from '@/entities/ReminderHistory';
  import { useReminderHistory } from '../model/useReminderHistory';
  import { useReminderHistorySelection } from '../model/useReminderHistorySelection';
  import type { ReminderHistoryFilters, ReminderTab } from '../model/types';
  import ReminderMyHistoryFilterModal from './ReminderMyHistoryFilterModal.vue';
  import ReminderHistoryToolbar from './ReminderHistoryToolbar.vue';
  import ReminderHistoryTable from './ReminderHistoryTable.vue';

  interface Props {
    initialTab?: ReminderTab;
  }

  const props = withDefaults(defineProps<Props>(), {
    initialTab: 'my-history',
  });

  const router = useRouter();

  const {
    activeTab,
    tabs,
    displayedNotices,
    isLoading,
    isExporting,
    currentPage,
    totalItems,
    itemsPerPage,
    isInfiniteScrollMode,
    loadingMore,
    hasMoreNotices,
    mapDate,
    expandedNoticeId,
    isRecipientsLoading,
    recipientErrorMessage,
    recipients,
    processingNoticeId,
    orderOptionsForTable,
    currentSort,
    currentOrder,
    isFilterActive,
    currentFilters,
    handleSearch,
    handlePageChange,
    handleItemsPerPageChange,
    handleExportToExcel,
    handleSortUpdate,
    handleFilterUpdate,
    toggleRecipientsRow,
    handleAcknowledge,
    reloadAfterBulkDelete,
    bulkDeleteApi,
  } = useReminderHistory(props.initialTab);

  const {
    isSelected,
    toggleSelection,
    toggleAll,
    allSelected,
    hasSelection,
    selectedIds,
    selectedCount,
    removeFromSelection,
    clearSelection,
  } = useReminderHistorySelection(() => displayedNotices.value);

  const onBulkDeleteSuccess = async (deletedIds: number[]) => {
    await reloadAfterBulkDelete(deletedIds.length);
  };

  const { openDeleteConfirmationModal } = useBulkDelete<NoticeGeneralHistoryItem>({
    entityName: {
      singular: 'Aviso',
      plural: 'Avisos',
    },
    successMessage: 'Aviso excluído com sucesso',
    successMessagePlural: 'Avisos excluídos com sucesso',
    bulkDeleteApi,
    onDeleteSuccess: onBulkDeleteSuccess,
    removeFromSelection,
    selectedCount: () => selectedCount.value,
    selectedIds: () => selectedIds.value,
  });

  const handleRowDelete = (noticeId: number) => {
    clearSelection();
    toggleSelection(noticeId);
    openDeleteConfirmationModal();
  };

  const handleDeleteSelected = () => {
    if (selectedIds.value.length === 0) {
      return;
    }

    openDeleteConfirmationModal();
  };

  const quantityOptions = [
    { id: 10, name: '10 por página' },
    { id: 25, name: '25 por página' },
    { id: 50, name: '50 por página' },
    { id: 100, name: 'Todos os avisos' },
  ];

  const searchFields = [{ id: 'message', name: 'Mensagem' }];

  const showGeneralColumns = computed(() => activeTab.value === 'general-history');

  const isGeneralHistoryTab = computed(() => activeTab.value === 'general-history');

  const isExportDisabled = computed(() => displayedNotices.value.length === 0 || isExporting.value);

  const reminderHistoryFilterModalAdapter = {
    clearFilters: () => {},
    filters: {},
  };

  const reminderHistoryFilterModalProps = {
    get showRecipientFilter() {
      return isGeneralHistoryTab.value;
    },
  };

  const { openFilterModal } = useTableFilterModal<ReminderHistoryFilters>(
    ReminderMyHistoryFilterModal,
    reminderHistoryFilterModalAdapter,
    (filters: ReminderHistoryFilters) => {
      handleFilterUpdate(filters);
    },
    () => currentFilters.value,
    reminderHistoryFilterModalProps
  );

  const handleOpenFilterModal = () => {
    openFilterModal();
  };

  const handleTabNavigation = async (tabId: string) => {
    if (tabId === 'my-history') {
      await router.push({ name: 'HistoricoAvisosMeu' });
      return;
    }

    if (tabId === 'general-history') {
      await router.push({ name: 'HistoricoAvisosGeral' });
    }
  };
</script>

<template>
  <div :class="isGeneralHistoryTab ? 'space-y-6' : ''">
    <ReminderHistoryToolbar
      :tabs="tabs"
      :active-tab="activeTab"
      :is-general-history-tab="isGeneralHistoryTab"
      :items-per-page="itemsPerPage"
      :quantity-options="quantityOptions"
      :search-fields="searchFields"
      :is-export-disabled="isExportDisabled"
      @tab-change="handleTabNavigation"
      @search="handleSearch"
      @items-per-page-change="handleItemsPerPageChange"
      @export-click="handleExportToExcel"
    />

    <ReminderHistoryTable
      :notices="displayedNotices"
      :is-loading="isLoading"
      :show-general-columns="showGeneralColumns"
      :expanded-notice-id="expandedNoticeId"
      :expanded-recipients="recipients"
      :is-recipients-loading="isRecipientsLoading"
      :recipient-error-message="recipientErrorMessage"
      :processing-notice-id="processingNoticeId"
      :map-date="mapDate"
      :order-options="orderOptionsForTable"
      :current-sort="currentSort"
      :current-order="currentOrder"
      :is-filter-active="isFilterActive"
      :all-selected="allSelected"
      :has-selection="hasSelection"
      :is-selected="isSelected"
      @update:sort="handleSortUpdate"
      @filter-clicked="handleOpenFilterModal"
      @open-recipients="toggleRecipientsRow"
      @toggle-all="toggleAll"
      @toggle-selection="toggleSelection"
      @delete-selected="handleDeleteSelected"
      @acknowledge="handleAcknowledge"
      @delete="handleRowDelete"
    />

    <template v-if="isInfiniteScrollMode">
      <div
        v-if="displayedNotices.length > 0"
        class="text-md pt-2 pl-8 text-gray-03"
      >
        Mostrando de 1 - {{ displayedNotices.length }} de {{ totalItems }} avisos
      </div>
    </template>

    <div
      v-show="isInfiniteScrollMode"
      id="infinite-scroll-trigger"
      class="py-4 text-center"
    >
      <div
        v-if="loadingMore"
        class="py-2"
      >
        <Loader2
          class="inline-block animate-spin mr-2 w-4 h-4"
          aria-hidden="true"
        />
        Carregando mais avisos...
      </div>
      <div
        v-else-if="!hasMoreNotices && displayedNotices.length > 0"
        class="py-2 text-gray-500"
      >
        Não há mais avisos para carregar
      </div>
      <div
        v-else-if="displayedNotices.length > 0"
        class="py-2 opacity-0"
      >
        Carregando...
      </div>
    </div>

    <Pagination
      v-if="!isInfiniteScrollMode && displayedNotices.length > 0"
      entity-name="aviso"
      :total-items="totalItems"
      :items-per-page="itemsPerPage"
      :current-page="currentPage"
      @page-changed="handlePageChange"
    />
  </div>
</template>
